import React from 'react';
import { Box, Typography } from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WidgetCard from './WidgetCard';
import { useWidgetData } from './useWidgetData';

interface ReportDay {
  /** YYYY-MM-DD */
  date: string;
  filled: boolean;
  revenue: number | null;
}

interface ReportTodayResponse {
  hasReport: boolean;
  /** [вчера, сегодня] */
  days: ReportDay[];
}

const ACCENT = '#0d9488';

const dayLabel = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

const Tile: React.FC<{ title: string; day: ReportDay; isToday: boolean }> = ({ title, day, isToday }) => {
  // Сегодняшний отчёт ещё может быть не заполнен - это не тревога; вчерашний - просрочен
  const overdue = !day.filled && !isToday;
  const color = day.filled ? '#15803d' : overdue ? '#b91c1c' : '#64748b';
  const bg = day.filled ? '#f0fdf4' : overdue ? '#fef2f2' : '#f8fafc';
  return (
    <Box sx={{ flex: 1, minWidth: 0, bgcolor: bg, borderRadius: '10px', px: 1.75, py: 1.25 }}>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.2 }}>{title}, {dayLabel(day.date)}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
        {day.filled
          ? <CheckCircleRoundedIcon sx={{ fontSize: 20, color }} />
          : <ErrorOutlineRoundedIcon sx={{ fontSize: 20, color }} />}
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
          {day.filled ? 'Отчёт заполнен' : overdue ? 'Не заполнен' : 'Ещё не заполнен'}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25, whiteSpace: 'nowrap' }}>
        {day.revenue != null ? `Z-отчёт: ${day.revenue.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₸` : 'Выручка не указана'}
      </Typography>
    </Box>
  );
};

/** Виджет «Отчёт за сегодня» (кабинет аптеки): статус дневного отчёта за сегодня и вчера. Право: home.report_today.view */
const ReportTodayWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<ReportTodayResponse>('/api/v2/home/report/today');
  const [yesterday, today] = data?.days || [];

  return (
    <WidgetCard
      title="Отчёт за сегодня"
      icon={<AssignmentTurnedInOutlinedIcon />}
      accent={ACCENT}
      loading={loading}
      error={error}
      onRefresh={reload}
      scroll={false}
    >
      {data && !data.hasReport ? (
        <Typography sx={{ p: 2, fontSize: 13, color: '#94a3b8' }}>Для этой учетной записи нет дневного отчета</Typography>
      ) : (
        yesterday && today && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, px: 2, py: 1.5 }}>
            <Tile title="Сегодня" day={today} isToday />
            <Tile title="Вчера" day={yesterday} isToday={false} />
          </Box>
        )
      )}
    </WidgetCard>
  );
};

export default ReportTodayWidget;
